import {
  attributeGroup,
  enumBuckets,
  lookupBucket,
  marker,
  numberAttr,
  pointLayer,
  rangeBucket,
  stringAttr,
  unsafeMapId,
  unsafeMapLayerId,
} from "@cartographerio/atlas-map";
import {
  WrtWestcountryCsiChannelSubstrateEnum,
  WrtWestcountryCsiInvasivePlantEnum,
  WrtWestcountryCsiPollutionEvidenceEnum,
  WrtWestcountryCsiPollutionSourceEnum,
  WrtWestcountryCsiWildlifeEnum,
} from "@cartographerio/inventory-enums";
import {
  cartographerMapSourceWithDefaults,
  mapSchemaWithDefaults,
} from "../../core";

export const WrtWestcountryCsi = mapSchemaWithDefaults({
  mapId: unsafeMapId("wrtWestcountryCsi"),
  title: "Westcountry CSI",
  layers: [
    pointLayer({
      layerId: unsafeMapLayerId("wrtWestcountryCsi"),
      title: "Westcountry CSI",
      source: cartographerMapSourceWithDefaults({
        layerId: unsafeMapLayerId("wrtWestcountryCsi"),
        attribution: [],
      }),
      attributes: [
        attributeGroup({
          label: "Plants and wildlife",
          attributes: [
            stringAttr({
              attributeId: "invasivePlant",
              label: "Invasive plants",
              buckets: enumBuckets(WrtWestcountryCsiInvasivePlantEnum),
            }),
            stringAttr({
              attributeId: "wildlife",
              label: "Wildlife",
              buckets: enumBuckets(WrtWestcountryCsiWildlifeEnum),
            }),
            stringAttr({
              attributeId: "outfalls",
              label: "Outfall(s)",
              buckets: [
                lookupBucket(null, marker.fromColor("#fff"), "None recorded"),
                lookupBucket(
                  "Active",
                  marker.fromColor("rgb(255, 128, 0)"),
                  "Active (discharging)"
                ),
                lookupBucket(
                  "Inactive",
                  marker.fromColor("rgb(255, 255, 0)"),
                  "Dormant (not discharging)"
                ),
              ],
            }),
          ],
        }),
        attributeGroup({
          label: "Pollution",
          attributes: [
            stringAttr({
              attributeId: "pollutionSource",
              label: "Visible sources of pollution",
              buckets: enumBuckets(WrtWestcountryCsiPollutionSourceEnum),
            }),
            stringAttr({
              attributeId: "pollutionEvidence",
              label: "Evidence of recent pollution",
              buckets: enumBuckets(WrtWestcountryCsiPollutionEvidenceEnum),
            }),
          ],
        }),
        attributeGroup({
          label: "River channel observations",
          attributes: [
            numberAttr({
              attributeId: "temperature",
              label: "Temperature",
              unit: "C",
              buckets: [
                rangeBucket(null, 4, marker.fromColor("#1094d8")),
                rangeBucket(4, 8, marker.quality(0.9)),
                rangeBucket(8, 12, marker.quality(0.5)),
                rangeBucket(12, 16, marker.quality(0.4)),
                rangeBucket(16, 20, marker.quality(0.3)),
                rangeBucket(20, 24, marker.quality(0.2)),
                rangeBucket(24, null, marker.quality(0.1)),
                lookupBucket(null, marker.empty, "-"),
              ],
            }),
            numberAttr({
              attributeId: "totalDissolvedSolids",
              label: "Total dissolved solids",
              unit: "ppm",
              buckets: [
                rangeBucket(null, 50, marker.fromColor("#1094d8")),
                rangeBucket(50, 200, marker.quality(0.9)),
                rangeBucket(200, 350, marker.quality(0.5)),
                rangeBucket(350, 500, marker.quality(0.4)),
                rangeBucket(500, 650, marker.quality(0.3)),
                rangeBucket(650, 800, marker.quality(0.2)),
                rangeBucket(800, null, marker.quality(0.1)),
                lookupBucket(null, marker.empty, "-"),
              ],
            }),
            numberAttr({
              attributeId: "turbidity",
              label: "Turbidity",
              unit: "NTU",
              buckets: [
                rangeBucket(null, 12, marker.fromColor("#1094d8")),
                rangeBucket(12, 40, marker.quality(0.9)),
                rangeBucket(40, 80, marker.quality(0.5)),
                rangeBucket(80, 120, marker.quality(0.4)),
                rangeBucket(120, 160, marker.quality(0.3)),
                rangeBucket(160, 240, marker.quality(0.2)),
                rangeBucket(240, null, marker.quality(0.1)),
                lookupBucket(null, marker.empty, "-"),
              ],
            }),
            numberAttr({
              attributeId: "phosphate",
              label: "Phosphates",
              unit: "ppb",
              buckets: [
                rangeBucket(null, 100, marker.fromColor("#1094d8")),
                rangeBucket(100, 200, marker.quality(0.9)),
                rangeBucket(200, 300, marker.quality(0.5)),
                rangeBucket(300, 500, marker.quality(0.4)),
                rangeBucket(500, 1000, marker.quality(0.3)),
                rangeBucket(1000, 2500, marker.quality(0.2)),
                rangeBucket(2500, null, marker.quality(0.1)),
                lookupBucket(null, marker.empty, "-"),
              ],
            }),
            stringAttr({
              attributeId: "predominateSubstrate",
              label: "Predominant substrate",
              buckets: enumBuckets(WrtWestcountryCsiChannelSubstrateEnum),
              showDistributionChart: false,
            }),
          ],
        }),
        attributeGroup({
          label: "Optional river channel observations",
          attributes: [
            numberAttr({
              attributeId: "ph",
              label: "pH",
              buckets: "auto",
            }),
            numberAttr({
              attributeId: "bacteria",
              label: "Bacteria",
              buckets: "auto",
            }),
            numberAttr({
              attributeId: "nitrate",
              label: "Nitrate",
              buckets: "auto",
            }),
            numberAttr({
              attributeId: "ammonia",
              label: "Ammonia",
              buckets: "auto",
            }),
          ],
        }),
      ],
    }),
  ],
});
